/**
 * RefutedSection — collapsed-by-default panel showing refuted
 * hypotheses for a playbook. The section is intentionally low-key —
 * refuted rules are useful negative evidence but shouldn't crowd the
 * detail page's primary surface.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Ban } from 'lucide-react';
import { type CreativeMemoryRefutedHypothesis } from '@/services/api';

interface RefutedSectionProps {
  refuted: CreativeMemoryRefutedHypothesis[];
}

export function RefutedSection({ refuted }: RefutedSectionProps) {
  const [open, setOpen] = useState(false);

  if (refuted.length === 0) {
    return null;
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-2">
          <Ban className="w-4 h-4 text-rose-500" />
          <span className="text-sm font-semibold text-slate-700">
            Refuted hypotheses
          </span>
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
            {refuted.length}
          </span>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-100 px-4 py-3">
          {refuted.map((h) => (
            <article
              key={h.hypothesis_id}
              className="rounded border border-slate-100 bg-slate-50 p-3"
            >
              <p className="text-sm font-medium text-slate-700">
                {h.hypothesis}
              </p>
              <p className="mt-1.5 text-xs text-slate-600 leading-snug">
                <span className="font-semibold text-rose-700">Outcome:</span>{' '}
                {h.outcome}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500">
                <span>
                  refuted{' '}
                  {h.refuted_at
                    ? new Date(h.refuted_at).toLocaleDateString()
                    : '—'}
                </span>
                {h.refutation_evidence.length > 0 && (
                  <span>
                    {h.refutation_evidence.length} negative episodes
                  </span>
                )}
                {h.refuted_from_rule_id && (
                  <span className="font-mono">
                    refuted_from: {h.refuted_from_rule_id}
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
